import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { adminApi, type AdminUserDetail, type AdminRole, type UserAdminRole } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { X, Save, Building2, KeyRound, Plus, Trash2, Monitor, Lock, Check, Copy, UserCheck } from "lucide-react";

interface AdminUserDetailModalProps {
  userId: string;
  onClose: () => void;
  onUpdated: () => void;
}

export function AdminUserDetailModal({ userId, onClose, onUpdated }: AdminUserDetailModalProps) {
  const navigate = useNavigate();
  const { user: currentUser, startImpersonation } = useAuth();
  const [userDetail, setUserDetail] = useState<AdminUserDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  // Edit form state
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);

  // Admin role assignment state
  const [userRoles, setUserRoles] = useState<UserAdminRole[]>([]);
  const [allRoles, setAllRoles] = useState<AdminRole[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [isAssigning, setIsAssigning] = useState(false);

  // Password reset state
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);

  useEffect(() => {
    loadUser();
  }, [userId]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const loadUser = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await adminApi.getUser(userId);
      if (response.success && response.data) {
        const u = response.data;
        setUserDetail(u);
        setEmail(u.email);
        setFirstName(u.firstName || "");
        setLastName(u.lastName || "");
        // Normalize legacy "admin" role to "user" (admin access is now via admin_roles)
        setRole(u.role === "admin" ? "user" : u.role);
        setEmailVerified(u.emailVerified);

        // Always load admin roles (any user can have admin roles assigned)
        await loadUserRoles();
      } else {
        setError(response.error?.message || "Kunde inte ladda användare");
      }
    } catch {
      setError("Kunde inte ansluta till servern");
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserRoles = async () => {
    try {
      const [rolesRes, allRolesRes] = await Promise.all([
        adminApi.getUserRoles(userId),
        adminApi.listRoles(),
      ]);
      if (rolesRes.success && rolesRes.data) setUserRoles(rolesRes.data);
      if (allRolesRes.success && allRolesRes.data) setAllRoles(allRolesRes.data);
    } catch {
      // Non-critical: roles section just won't populate
    }
  };

  const handleAssignRole = async () => {
    if (!selectedRoleId) return;
    setIsAssigning(true);
    try {
      const response = await adminApi.assignRoleToUser(userId, selectedRoleId);
      if (response.success) {
        setSelectedRoleId("");
        await loadUserRoles();
        toast.success("Roll tilldelad");
      }
    } catch {
      toast.error("Kunde inte tilldela roll");
    } finally {
      setIsAssigning(false);
    }
  };

  const handleRemoveRole = async (roleId: string) => {
    setIsAssigning(true);
    try {
      const response = await adminApi.removeRoleFromUser(userId, roleId);
      if (response.success) {
        await loadUserRoles();
        toast.success("Roll borttagen");
      }
    } catch {
      toast.error("Kunde inte ta bort roll");
    } finally {
      setIsAssigning(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const response = await adminApi.updateUser(userId, {
        email,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        role,
        emailVerified,
      });
      if (response.success) {
        onUpdated();
        toast.success("Användaren har uppdaterats");
        onClose();
      } else {
        setError(response.error?.message || "Kunde inte uppdatera användare");
      }
    } catch {
      toast.error("Kunde inte ansluta till servern");
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!confirm("Är du säker på att du vill inaktivera denna användare?")) return;
    setIsSaving(true);
    setError(null);
    try {
      const response = await adminApi.deleteUser(userId);
      if (response.success) {
        onUpdated();
        toast.success("Användaren har inaktiverats");
        await loadUser();
      } else {
        toast.error(response.error?.message || "Kunde inte inaktivera användare");
      }
    } catch {
      toast.error("Kunde inte ansluta till servern");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRestore = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const response = await adminApi.restoreUser(userId);
      if (response.success) {
        onUpdated();
        toast.success("Användaren har återställts");
        await loadUser();
      } else {
        toast.error(response.error?.message || "Kunde inte återställa användare");
      }
    } catch {
      toast.error("Kunde inte ansluta till servern");
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 10) {
      setError("Lösenordet måste vara minst 10 tecken");
      return;
    }
    setIsResettingPassword(true);
    setError(null);
    try {
      const response = await adminApi.resetUserPassword(userId, newPassword);
      if (response.success) {
        setPasswordResetSuccess(true);
        toast.success("Lösenordet har återställts");
      } else {
        toast.error(response.error?.message || "Kunde inte återställa lösenord");
      }
    } catch {
      toast.error("Kunde inte ansluta till servern");
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(newPassword);
      setCopiedPassword(true);
      setTimeout(() => setCopiedPassword(false), 2000);
    } catch {
      // Fallback for older browsers
    }
  };

  const generateRandomPassword = () => {
    const chars = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%";
    let password = "";
    for (let i = 0; i < 14; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
    setPasswordResetSuccess(false);
  };

  const parseUserAgent = (ua: string | null): string => {
    if (!ua) return "Okänd enhet";
    if (ua.includes("Chrome")) return "Chrome";
    if (ua.includes("Firefox")) return "Firefox";
    if (ua.includes("Safari")) return "Safari";
    if (ua.includes("Edge")) return "Edge";
    return "Webbläsare";
  };

  const handleImpersonate = async () => {
    if (!confirm(`Är du säker på att du vill logga in som ${userDetail?.email}? Du kommer att se applikationen som denna användare.`)) {
      return;
    }

    setIsImpersonating(true);
    try {
      const response = await adminApi.impersonateUser(userId);
      if (response.success && response.data) {
        startImpersonation(response.data);
        toast.success(`Du är nu inloggad som ${response.data.user.email}`);
        onClose();
        navigate("/dashboard");
      } else {
        toast.error(response.error?.message || "Kunde inte starta impersonation");
      }
    } catch {
      toast.error("Kunde inte ansluta till servern");
    } finally {
      setIsImpersonating(false);
    }
  };

  const isSelf = currentUser?.id === userId;
  const canChangeRole = currentUser?.role === "super_admin" && !isSelf;
  const isSuperAdmin = currentUser?.role === "super_admin";

  // Track mousedown to prevent closing when dragging from inside modal
  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownTargetRef.current = e.target;
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (
      e.target === overlayRef.current &&
      mouseDownTargetRef.current === overlayRef.current
    ) {
      onClose();
    }
    mouseDownTargetRef.current = null;
  };

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/20"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Användardetaljer</h2>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-100 transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : error && !userDetail ? (
            <div className="text-center py-8">
              <p className="text-red-600">{error}</p>
            </div>
          ) : userDetail ? (
            <div className="space-y-5">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">E-post</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Name fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Förnamn</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Efternamn</label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Roll</label>
                {canChangeRole ? (
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="user">Användare</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                ) : (
                  <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-lg capitalize">
                    {role === "super_admin" ? "Super Admin" : "Användare"}
                    {isSelf && <span className="text-gray-400 ml-2">(kan inte ändra egen roll)</span>}
                  </p>
                )}
              </div>

              {/* Email verified */}
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={emailVerified}
                    onChange={(e) => setEmailVerified(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-700">E-post verifierad</span>
                </label>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <div className="flex items-center gap-3">
                  {userDetail.deletedAt ? (
                    <>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                        Borttagen
                      </span>
                      <button
                        type="button"
                        onClick={handleRestore}
                        disabled={isSaving}
                        className="text-sm text-green-600 hover:text-green-700 font-medium"
                      >
                        Återställ användare
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        Aktiv
                      </span>
                      {!isSelf && (
                        <button
                          type="button"
                          onClick={handleToggleStatus}
                          disabled={isSaving}
                          className="text-sm text-red-600 hover:text-red-700 font-medium"
                        >
                          Inaktivera användare
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Password Reset */}
              {isSuperAdmin && !isSelf && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Lösenord</h3>
                  {!showPasswordReset ? (
                    <button
                      type="button"
                      onClick={() => {
                        setShowPasswordReset(true);
                        generateRandomPassword();
                      }}
                      className="inline-flex items-center gap-1.5 text-sm text-amber-600 hover:text-amber-700 font-medium"
                    >
                      <Lock size={14} />
                      Återställ lösenord
                    </button>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={newPassword}
                          onChange={(e) => {
                            setNewPassword(e.target.value);
                            setPasswordResetSuccess(false);
                          }}
                          placeholder="Nytt lösenord (minst 10 tecken)"
                          className="flex-1 px-3 py-1.5 border border-amber-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white"
                        />
                        <button
                          type="button"
                          onClick={handleCopyPassword}
                          className="p-1.5 rounded hover:bg-amber-100 transition-colors"
                          title="Kopiera"
                        >
                          {copiedPassword ? <Check size={14} className="text-green-600" /> : <Copy size={14} className="text-amber-600" />}
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={generateRandomPassword}
                          className="text-xs text-amber-600 hover:text-amber-700 font-medium"
                        >
                          Generera nytt
                        </button>
                        <div className="flex-1" />
                        <button
                          type="button"
                          onClick={() => {
                            setShowPasswordReset(false);
                            setNewPassword("");
                            setPasswordResetSuccess(false);
                          }}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          Avbryt
                        </button>
                        <button
                          type="button"
                          onClick={handleResetPassword}
                          disabled={isResettingPassword || !newPassword || newPassword.length < 10}
                          className="inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {isResettingPassword ? <Spinner size="sm" /> : "Spara lösenord"}
                        </button>
                      </div>
                      {passwordResetSuccess && (
                        <p className="text-xs text-green-600 font-medium">
                          Lösenordet har återställts. Kopiera och skicka det till användaren.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ID & Dates */}
              <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                <p className="text-xs text-gray-500">
                  <span className="font-medium">ID:</span> {userDetail.id}
                </p>
                <p className="text-xs text-gray-500">
                  <span className="font-medium">Skapad:</span>{" "}
                  {new Date(userDetail.createdAt).toLocaleString("sv-SE")}
                </p>
                {userDetail.deletedAt && (
                  <p className="text-xs text-red-500">
                    <span className="font-medium">Borttagen:</span>{" "}
                    {new Date(userDetail.deletedAt).toLocaleString("sv-SE")}
                  </p>
                )}
              </div>

              {/* Companies */}
              {userDetail.companies.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Företag</h3>
                  <div className="space-y-2">
                    {userDetail.companies.map((c) => (
                      <div
                        key={c.id}
                        className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg"
                      >
                        <Building2 size={14} className="text-gray-400" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{c.name}</p>
                          <p className="text-xs text-gray-500">{c.organizationNumber}</p>
                        </div>
                        <span className="text-xs text-gray-400 capitalize">{c.role}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Active Sessions */}
              {userDetail.activeSessions && userDetail.activeSessions.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">
                    Aktiva sessioner ({userDetail.activeSessions.length})
                  </h3>
                  <div className="space-y-2">
                    {userDetail.activeSessions.map((s) => (
                      <div
                        key={s.id}
                        className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg"
                      >
                        <Monitor size={14} className="text-gray-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900">{parseUserAgent(s.userAgent)}</p>
                          <p className="text-xs text-gray-500">
                            {s.ipAddress || "Okänd IP"}
                            {" · "}
                            Skapad {new Date(s.createdAt).toLocaleDateString("sv-SE")}
                          </p>
                        </div>
                        <span className="text-xs text-gray-400 shrink-0">
                          Går ut {new Date(s.expiresAt).toLocaleDateString("sv-SE")}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Admin Roles (managed by super_admin — any user can have admin roles) */}
              {isSuperAdmin && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Adminroller</h3>
                  {userRoles.length > 0 ? (
                    <div className="space-y-2 mb-3">
                      {userRoles.map((ur) => (
                        <div
                          key={ur.roleId}
                          className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <KeyRound size={14} className="text-amber-500 shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{ur.roleName}</p>
                              {ur.roleDescription && (
                                <p className="text-xs text-gray-500 truncate">{ur.roleDescription}</p>
                              )}
                            </div>
                          </div>
                          {!isSelf && (
                            <button
                              type="button"
                              onClick={() => handleRemoveRole(ur.roleId)}
                              disabled={isAssigning}
                              className="p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors shrink-0"
                              title="Ta bort roll"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 mb-3">Inga roller tilldelade</p>
                  )}

                  {/* Assign new role */}
                  {!isSelf && allRoles.filter((r) => !userRoles.some((ur) => ur.roleId === r.id) && !r.deletedAt).length > 0 && (
                    <div className="flex items-center gap-2">
                      <select
                        value={selectedRoleId}
                        onChange={(e) => setSelectedRoleId(e.target.value)}
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="">Välj roll...</option>
                        {allRoles
                          .filter((r) => !userRoles.some((ur) => ur.roleId === r.id) && !r.deletedAt)
                          .map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.name}
                            </option>
                          ))}
                      </select>
                      <button
                        type="button"
                        onClick={handleAssignRole}
                        disabled={!selectedRoleId || isAssigning}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <Plus size={14} />
                        Tilldela
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Error message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        {userDetail && (
          <div className="flex items-center justify-between p-5 border-t border-gray-200">
            {/* Impersonate button (super_admin only, not for self, not for deleted users) */}
            <div>
              {isSuperAdmin && !isSelf && !userDetail.deletedAt && (
                <Button
                  variant="outline"
                  onClick={handleImpersonate}
                  disabled={isImpersonating}
                  className="text-purple-600 border-purple-300 hover:bg-purple-50 hover:border-purple-400"
                >
                  {isImpersonating ? <Spinner size="sm" /> : <UserCheck size={16} />}
                  Logga in som
                </Button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={onClose}>
                Avbryt
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? <Spinner size="sm" /> : <Save size={16} />}
                Spara
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
